import {
  DefaultValues,
  FieldValues,
  Mode,
  Resolver,
  SubmitErrorHandler,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { GenericStep, UseStepperFormProps } from './types';
import { useCallback, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { collectDynamicFieldStates } from '../questionnaire/hooks/useQuestionnaire';
import { DynamicQuestionnaireDependencie } from '../dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import { useLocation, useNavigate } from 'react-router-dom';

const findInitialStep = <T extends FieldValues>(
  steps: GenericStep[],
  initialValues: DefaultValues<T>,
): number => {
  if (!initialValues || Object.keys(initialValues).length === 0) {
    return 0;
  }
  for (let i = steps.length - 1; i >= 0; i--) {
    const stepSchema = steps[i].schema;
    const validate = stepSchema.validateSync.bind(stepSchema);

    try {
      if (steps[i].skipOption) {
        throw new Error();
      }
      validate(initialValues);
      return Math.min(i + 1, steps.length - 1);
    } catch (e) {
      console.warn(i, e);
    }
  }
  return 0;
};

export const useFormErrors = <T extends FieldValues>() => {
  const onError: SubmitErrorHandler<T> = (errors) => {
    const errorKeys = Object.keys(errors) as (keyof T)[];
    if (errorKeys.length > 0) {
      const firstErrorField = errorKeys[0];
      const element = document.getElementById(firstErrorField.toString());
      if (element) {
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };
  return { onError };
};

export const useQuestionnaireStepperForm = <T extends FieldValues>({
  steps,
  initialValues,
  values,
  leaveCallback,
  mode = 'onChange',
  top = 0,
}: UseStepperFormProps<T>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { onError } = useFormErrors<T>();
  const currentStep = location.state ?? 0;
  const currentStepMode: Mode = steps[currentStep]?.mode ?? mode;

  const dynamicResolver: Resolver<T> = async (values, context, options) => {
    const currentSchema = steps[currentStep].schema;
    return yupResolver(currentSchema)(values, context, options);
  };

  const methods = useForm<T>({
    resolver: dynamicResolver,
    mode: currentStepMode,
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    values: values,
  });

  const handleNextStep = async () => {
    const onStepSubmit = (data: T) => {
      if (currentStep < steps.length - 1) {
        const nextStep = currentStep + 1;
        navigate({ pathname: location.pathname }, { state: nextStep });

        leaveCallback && leaveCallback(data);

        window.scrollTo({
          top,
          left: 0,
          behavior: 'smooth',
        });

        setTimeout(
          () =>
            methods.reset(undefined, {
              keepValues: true,
              keepDefaultValues: true,
              keepDirty: true,
              keepTouched: false,
              keepErrors: false,
              keepSubmitCount: false,
              keepIsSubmitted: false,
            }),
          10,
        );
      }
    };
    return methods.handleSubmit(onStepSubmit, onError)();
  };

  return {
    currentStep: currentStep,
    handleNextStep,
    onError,
    methods,
  };
};

export const useStepperForm = <T extends FieldValues>({
  steps,
  initialValues,
  values,
  leaveCallback,
  searchLastSavedStep = true,
  manualInitialStep,
  mode = 'onChange',
  top = 0,
}: UseStepperFormProps<T>) => {
  const { onError } = useFormErrors<T>();
  const initialStep = useMemo(
    () =>
      manualInitialStep ??
      (searchLastSavedStep ? findInitialStep(steps, initialValues) : 0),
    [manualInitialStep, searchLastSavedStep, steps, initialValues],
  );

  const [currentStep, setCurrentStep] = useState(() => initialStep || 0);
  const [highestValidatedStep, setHighestValidatedStep] =
    useState<number>(initialStep);

  const dynamicResolver: Resolver<T> = async (values, context, options) => {
    const currentSchema = steps[currentStep].schema;
    return yupResolver(currentSchema)(values, context, options);
  };

  const methods = useForm<T>({
    resolver: dynamicResolver,
    mode: mode,
    reValidateMode: 'onChange',
    defaultValues: initialValues,
    values: values,
  });

  const handleNextStep = useCallback(async () => {
    const onStepSubmit = (data: T) => {
      if (currentStep < steps.length - 1) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        setHighestValidatedStep((prev) => Math.max(prev, nextStep));

        leaveCallback && leaveCallback(data);

        window.scrollTo({
          top,
          left: 0,
          behavior: 'smooth',
        });

        setTimeout(
          () =>
            methods.reset(undefined, {
              keepValues: true,
              keepDefaultValues: true,
              keepDirty: true,
              keepTouched: false,
              keepErrors: false,
              keepSubmitCount: false,
              keepIsSubmitted: false,
            }),
          10,
        );
      }
    };
    return methods.handleSubmit(onStepSubmit, onError)();
  }, [currentStep, leaveCallback, methods, onError, steps.length, top]);

  const handleGoToStep = async (targetIndex: number) => {
    const isValid = await methods.trigger();

    if (targetIndex <= highestValidatedStep && isValid) {
      setTimeout(
        () =>
          methods.reset(undefined, {
            keepValues: true,
            keepDefaultValues: true,
            keepDirty: true,
            keepTouched: false,
            keepErrors: false,
            keepSubmitCount: false,
            keepIsSubmitted: false,
          }),
        10,
      );
      return setCurrentStep(targetIndex);
    }
  };

  const skipStep = () => {
    const lastStep = currentStep === steps.length - 1;
    if (lastStep) {
      return () => ({});
    }
    setHighestValidatedStep((prev) => prev + 1);
    return setCurrentStep((currentStep) => currentStep + 1);
  };

  return {
    currentStep,
    handleNextStep,
    handleGoToStep,
    onError,
    skipStep,
    methods,
    highestValidatedStep,
  };
};

export const useGetDynamicDeps = (
  sectionDependencies: DynamicQuestionnaireDependencie[],
) => {
  const { control } = useFormContext();

  const controlIds = useMemo(() => {
    const ids: string[] = [];
    for (const dep of sectionDependencies) {
      const props = dep?.if?.properties ?? {};
      for (const id of Object.keys(props)) {
        ids.push(id);
      }
    }
    return Array.from(new Set(ids));
  }, [sectionDependencies]);

  const watchedControlValues = useWatch({
    control: control,
    name: controlIds,
  });

  const controlValuesMap = useMemo(() => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const map: Record<string, any> = {};
    if (Array.isArray(watchedControlValues)) {
      controlIds.forEach((id, idx) => {
        map[id] = watchedControlValues[idx];
      });
    } else if (typeof watchedControlValues !== 'undefined') {
      map[controlIds[0]] = watchedControlValues;
    }
    return map;
  }, [watchedControlValues, controlIds]);

  return useMemo(() => {
    return collectDynamicFieldStates(sectionDependencies, controlValuesMap);
  }, [sectionDependencies, controlValuesMap]);
};
